import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { Button, Input } from "@/shared/ui";
import { supabase } from "@/shared/db/supabase-client";
import { updateProfile, createOnboardingProgress } from "@/shared/db";
import { toast } from "sonner";

type VerificationStatus =
  | "success"
  | "expired"
  | "invalid"
  | "loading"
  | "resend"
  | "pending";

export const EmailVerificationPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<VerificationStatus>("loading");
  const [email, setEmail] = useState<string>("");
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    const handleAuthCallback = async () => {
      // Check URL parameters for error
      const error = searchParams.get("error");
      const errorCode = searchParams.get("error_code");

      // Also check hash parameters (Supabase uses hash for tokens)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const hashError = hashParams.get("error");
      const hashErrorCode = hashParams.get("error_code");
      const accessToken = hashParams.get("access_token");
      const refreshToken = hashParams.get("refresh_token");
      const type = hashParams.get("type");

      const finalError = error || hashError;
      const finalErrorCode = errorCode || hashErrorCode;

      // Handle errors
      if (finalError === "access_denied") {
        if (finalErrorCode === "otp_expired") {
          setStatus("expired");
        } else {
          setStatus("invalid");
        }
        return;
      }

      // Handle successful verification with access_token
      if (accessToken && refreshToken && type === "signup") {
        try {
          // Set the session with the tokens from the URL
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) {
            console.error("Session error:", sessionError);
            setStatus("invalid");
            return;
          }

          setStatus("success");
          // Redirect: use type param from query (Supabase hash has type=signup)
          const typeParam = searchParams.get("type");
          const onboardingPath =
            typeParam === "lawyer" || typeParam === "client"
              ? `/onboarding/${typeParam}`
              : "/onboarding/select-type";

          // If we have type, set profile + onboarding so OnboardingRoute doesn't redirect to select-type
          if (typeParam === "lawyer" || typeParam === "client") {
            try {
              const {
                data: { user: authUser },
              } = await supabase.auth.getUser();
              if (authUser) {
                const meta = authUser.user_metadata || {};
                await updateProfile(authUser.id, {
                  user_type: typeParam,
                  ...(meta.country && { country: meta.country }),
                });
                const totalSteps = typeParam === "lawyer" ? 7 : 1;
                await createOnboardingProgress(authUser.id, totalSteps);
              }
            } catch (e) {
              console.warn("Could not pre-set profile for onboarding:", e);
            }
          }

          setTimeout(() => {
            navigate(onboardingPath);
          }, 2000);
        } catch (err) {
          console.error("Error setting session:", err);
          setStatus("invalid");
        }
        return;
      }

      // No error and no token - check if llegó tras registro (pending) o enlace inválido
      const emailParam = searchParams.get("email");
      const fromSignup = searchParams.get("from") === "signup";
      if (emailParam && fromSignup) {
        setEmail(emailParam);
        setStatus("pending");
      } else {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setStatus("success");
          const typeParam = searchParams.get("type");
          const onboardingPath =
            typeParam === "lawyer" || typeParam === "client"
              ? `/onboarding/${typeParam}`
              : "/onboarding/select-type";
          setTimeout(() => navigate(onboardingPath), 2000);
        } else {
          if (emailParam) {
            setEmail(emailParam);
            setStatus("pending");
          } else {
            setStatus("invalid");
          }
        }
      }
    };

    handleAuthCallback();
  }, [searchParams, navigate]);

  const handleResendEmail = async () => {
    if (!email) {
      toast.error(t("auth.verification.enterEmail"));
      return;
    }

    setIsResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/verify`,
        },
      });

      if (error) {
        throw error;
      }

      toast.success(t("auth.verification.resendSuccess"));
      setStatus("resend");
    } catch (err) {
      const msg = (err as { message?: string })?.message;
      console.error("Error resending email:", err);
      toast.error(msg || t("auth.verification.resendError"), {
        duration: 6000,
      });
    } finally {
      setIsResending(false);
    }
  };

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 border-4 border-tiber-600 border-t-transparent rounded-full animate-spin" />
            <p className="mt-4 text-tiber-700">
              {t("auth.verification.verifying")}
            </p>
          </div>
        );

      case "success":
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="mx-auto w-20 h-20 bg-atlantis-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircleIcon className="w-12 h-12 text-atlantis-600" />
            </div>
            <h1 className="text-2xl font-bold text-juridico mb-2">
              {t("auth.verification.successTitle")}
            </h1>
            <p className="text-tiber-700 mb-6">
              {t("auth.verification.successMessage")}
            </p>
            <p className="text-sm text-titan">
              {t("auth.verification.redirecting")}
            </p>
          </motion.div>
        );

      case "expired":
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="mx-auto w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mb-6">
              <ExclamationTriangleIcon className="w-12 h-12 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-juridico mb-2">
              {t("auth.verification.expiredTitle")}
            </h1>
            <p className="text-tiber-700 mb-6">
              {t("auth.verification.expiredMessage")}
            </p>

            <div className="space-y-4">
              <div className="flex flex-col gap-2">
                <Input
                  label={t("auth.verification.emailLabel")}
                  id="resend-email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("auth.verification.emailPlaceholder")}
                />
              </div>

              <Button
                onClick={handleResendEmail}
                disabled={isResending || !email}
                className="w-full"
              >
                {isResending ? (
                  <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <EnvelopeIcon className="w-5 h-5 mr-2" />
                )}
                {t("auth.verification.resendButton")}
              </Button>
            </div>
          </motion.div>
        );

      case "invalid":
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <ExclamationTriangleIcon className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-juridico mb-2">
              {t("auth.verification.invalidTitle")}
            </h1>
            <p className="text-tiber-700 mb-6">
              {t("auth.verification.invalidMessage")}
            </p>

            <div className="space-y-3">
              <Button
                onClick={() => navigate("/auth")}
                variant="primary"
                className="w-full"
              >
                {t("auth.verification.tryAgain")}
              </Button>
              <Button
                onClick={() => navigate("/")}
                variant="secondary"
                className="w-full"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                {t("auth.verification.backToHome")}
              </Button>
            </div>
          </motion.div>
        );

      case "pending":
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="mx-auto w-20 h-20 bg-tiber-100 rounded-full flex items-center justify-center mb-6">
              <EnvelopeIcon className="w-12 h-12 text-tiber-600" />
            </div>
            <h1 className="text-2xl font-bold text-juridico mb-2">
              {t("auth.emailVerification.title")}
            </h1>
            <p className="text-tiber-700 mb-6">
              {t("auth.emailVerification.description")}
            </p>
            <div className="space-y-4">
              <div className="flex flex-col gap-2">
                <Input
                  label={t("auth.verification.emailLabel")}
                  id="pending-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("auth.verification.emailPlaceholder")}
                  disabled={true}
                />
              </div>
              <Button
                onClick={handleResendEmail}
                disabled={isResending || !email}
                className="w-full"
              >
                {isResending ? (
                  <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <EnvelopeIcon className="w-5 h-5 mr-2" />
                )}
                {t("auth.verification.resendButton")}
              </Button>
            </div>
          </motion.div>
        );

      case "resend":
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="mx-auto w-20 h-20 bg-tiber-100 rounded-full flex items-center justify-center mb-6">
              <EnvelopeIcon className="w-12 h-12 text-tiber-600" />
            </div>
            <h1 className="text-2xl font-bold text-juridico mb-2">
              {t("auth.verification.resendTitle")}
            </h1>
            <p className="text-tiber-700 mb-6">
              {t("auth.verification.resendMessage", { email })}
            </p>
            <p className="text-sm text-titan mb-6">
              {t("auth.verification.checkSpam")}
            </p>
            <Button
              onClick={() => navigate("/")}
              variant="secondary"
              className="w-full"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              {t("auth.verification.backToHome")}
            </Button>
          </motion.div>
        );
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-hero flex items-center justify-center p-4 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-tiber-500/20 blur-3xl" />
        <div className="absolute bottom-1/4 -left-32 w-80 h-80 rounded-full bg-tiber-400/15 blur-3xl" />
      </div>
      <div className="absolute inset-0 opacity-20 overflow-hidden pointer-events-none">
        <div className="absolute top-1/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/40 to-transparent animate-pulse" />
        <div
          className="absolute top-2/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/30 to-transparent animate-pulse"
          style={{ animationDelay: "0.8s" }}
        />
        <div
          className="absolute top-3/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/35 to-transparent animate-pulse"
          style={{ animationDelay: "1.6s" }}
        />
        <div
          className="absolute top-4/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/25 to-transparent animate-pulse"
          style={{ animationDelay: "2.4s" }}
        />
        <div className="absolute inset-0">
          <div
            className="absolute top-1/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-white/25 to-transparent rotate-12 origin-left"
            style={{ animation: "slide 18s linear infinite" }}
          />
          <div
            className="absolute top-2/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-white/30 to-transparent -rotate-12 origin-left"
            style={{
              animation: "slide-reverse 22s linear infinite",
              animationDelay: "3s",
            }}
          />
        </div>
        <div
          className="absolute top-0 left-1/5 w-px h-full bg-linear-to-b from-transparent via-white/20 to-transparent animate-pulse"
          style={{ animationDelay: "0.3s" }}
        />
        <div
          className="absolute top-0 left-1/2 w-px h-full bg-linear-to-b from-transparent via-white/25 to-transparent animate-pulse"
          style={{ animationDelay: "1.2s" }}
        />
        <div
          className="absolute top-0 left-4/5 w-px h-full bg-linear-to-b from-transparent via-white/20 to-transparent animate-pulse"
          style={{ animationDelay: "1.8s" }}
        />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-tiber-100">
          {renderContent()}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-titan">
            {t("auth.verification.needHelp")}{" "}
            <a
              href="mailto:nolimstudios@gmail.com"
              className="text-tiber-600 hover:underline"
            >
              Nolim Studios Support
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default EmailVerificationPage;
